import {
  IContentCard,
  IContentCatalogItem,
  IContentPageConfig,
  IContentPageConfigSource,
} from "../../types/Content";

/**
 * Base URL for static content (JSON configs, translations, images). Empty in
 * local dev so paths resolve against the app origin (i.e. `public/`). The
 * `VITE_CONTENT_BASE_URL` variable points at the content storage account.
 */
export const CONTENT_BASE_URL: string = (import.meta.env.VITE_CONTENT_BASE_URL ?? "").replace(/\/$/, "");

const ABSOLUTE_URL_REGEX = /^https?:\/\//i;

/**
 * Resolves a content path against `CONTENT_BASE_URL`. Absolute URLs are
 * returned unchanged so JSON entries can opt-in to hard-coded external URLs.
 */
export const resolveContentUrl = (path: string): string => {
  if (!path) return path;
  if (ABSOLUTE_URL_REGEX.test(path)) return path;

  const normalized = path.startsWith("/") ? path : `/${path}`;
  return `${CONTENT_BASE_URL}${normalized}`;
};

/**
 * Fetches a content path, trying the CMS storage first and falling back to the
 * SWA origin (same relative path under `public/`) on any failure — network
 * error or non-ok response. Absolute URLs bypass the fallback.
 */
export const fetchContentWithFallback = async (
  path: string,
  init?: RequestInit,
): Promise<Response> => {
  const isAbsolute = ABSOLUTE_URL_REGEX.test(path);
  const normalized = path.startsWith("/") ? path : `/${path}`;
  const primaryUrl = isAbsolute ? path : resolveContentUrl(path);
  const originUrl = isAbsolute ? path : normalized;

  if (isAbsolute || primaryUrl === originUrl) {
    return fetch(primaryUrl, init);
  }

  try {
    const response = await fetch(primaryUrl, init);
    if (response.ok) {
      return response;
    }
    console.warn(
      `[content] Primary fetch failed (${response.status}) for ${primaryUrl}, falling back to origin.`,
    );
  } catch (error) {
    console.warn(
      `[content] Primary fetch threw for ${primaryUrl}, falling back to origin.`,
      error,
    );
  }

  return fetch(originUrl, init);
};

const createCatalogLookup = (catalog: IContentCatalogItem[]): Map<string, string> =>
  new Map(catalog.map(item => [item.id, item.label]));

export const resolveContentPageConfig = (
  config: IContentPageConfigSource,
): IContentPageConfig => {
  const categories = createCatalogLookup(config.categories);
  const toolTypes = createCatalogLookup(config.toolTypes);
  const environmentalThemes = createCatalogLookup(config.environmentalThemes);

  const cards: IContentCard[] = config.cards.map(card => ({
    ...card,
    category: card.category.map(id => categories.get(id) ?? id),
    toolType: card.toolType.map(id => toolTypes.get(id) ?? id),
    environmentalTheme: card.environmentalTheme.map(id =>
      environmentalThemes.get(id) ?? id,
    ),
  }));

  return { tabs: config.tabs, cards };
};
